import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquare, AlertTriangle } from "lucide-react";
import { ReactorCore } from "@/components/command-center/hud/ReactorCore";
import { TopHud } from "@/components/command-center/hud/TopHud";
import { HudPanel } from "@/components/command-center/hud/HudPanel";
import {
  SystemHealthPanel, VpsFleetPanel, NetworkSecurityPanel, ActiveAgentsPanel,
  ApprovalsPanel, RecentActivityPanel, AiUsagePanel, QuickCommandsPanel,
} from "@/components/command-center/hud/SidePanels";
import { ConversationPanel } from "@/components/command-center/ConversationPanel";
import { apiGet, protectedPost } from "@/lib/api";

export default function CommandCenter() {
  const [coreState, setCoreState] = useState("idle");
  const [emergency, setEmergency] = useState(false);
  const [health, setHealth] = useState(null);
  const [aiStatus, setAiStatus] = useState(null);
  const [topTask, setTopTask] = useState(null);
  const [micOn, setMicOn] = useState(false);
  const [emgConfirm, setEmgConfirm] = useState(false);
  const assistantRef = useRef(null);
  const navigate = useNavigate();

  const loadHealth = useCallback(() => apiGet("/system/health").then((s) => {
    setHealth(s);
    setEmergency(!!s.emergency);
  }).catch(() => setHealth(null)), []);

  useEffect(() => {
    loadHealth();
    const id = setInterval(loadHealth, 5000);
    return () => clearInterval(id);
  }, [loadHealth]);

  useEffect(() => {
    apiGet("/system/status").then(setAiStatus).catch(() => setAiStatus(null));
  }, []);

  useEffect(() => {
    const load = () => apiGet("/tasks").then((t) => setTopTask(t.find((x) => x.status === "running") || null)).catch(() => {});
    load();
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, []);

  const toggleEmergency = async () => {
    if (!emergency) {
      setEmgConfirm(true);
      return;
    }
    try {
      await protectedPost("/emergency/restore");
      setEmergency(false);
      setHealth(null);
      loadHealth();
      toast.success("Previously enabled Jarvis workers restored, Sir.");
    } catch {
      toast.error("Emergency action failed — please ensure you're unlocked.");
    }
  };

  const confirmEmergency = async () => {
    setEmgConfirm(false);
    try {
      await protectedPost("/emergency/shutdown-all");
      setEmergency(true);
      loadHealth();
      toast.error("Emergency pause engaged — Jarvis workers stopped, Sir.");
    } catch {
      toast.error("Emergency action failed — please ensure you're unlocked.");
    }
  };

  const onQuick = (kind) => {
    const a = assistantRef.current;
    if (!a) return;
    if (kind === "briefing") a.runBriefing();
    else if (kind === "status") a.sendText("Report system status");
    else if (kind === "focus") a.focus();
  };

  const onMic = () => {
    assistantRef.current?.focus();
    assistantRef.current?.toggleClap?.();
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <TopHud aiStatus={aiStatus} />

      <div className="flex-1 min-h-0 overflow-y-auto min-[1400px]:overflow-hidden">
        <div className="min-[1400px]:h-full grid grid-cols-1 min-[1400px]:grid-cols-[minmax(360px,27.5fr)_minmax(600px,45fr)_minmax(360px,27.5fr)] gap-4 p-3">
          {/* LEFT panels */}
          <div className="order-2 min-[1400px]:order-1 space-y-3.5 min-[1400px]:h-full min-[1400px]:overflow-y-auto pr-0.5" data-testid="hud-left-col">
            <SystemHealthPanel />
            <VpsFleetPanel />
            <NetworkSecurityPanel />
            <ActiveAgentsPanel onOpenAgents={() => navigate("/agents")} />
          </div>

          {/* CENTER — reactor core */}
          <div className="order-1 min-[1400px]:order-2 relative min-h-[64vh] min-[1400px]:min-h-0 min-[1400px]:h-full" data-testid="hud-core-col">
            <ReactorCore
              state={coreState !== "idle" ? coreState : topTask ? "executing" : micOn ? "listening" : "idle"}
              emergency={emergency}
              health={health}
              onNavigate={(p) => navigate(p)}
              onQuick={onQuick}
              onEmergencyToggle={toggleEmergency}
              onMic={onMic}
              micOn={micOn}
              taskLabel={topTask ? `${topTask.agent} · ${topTask.title}` : null}
            />
          </div>

          {/* RIGHT panels */}
          <div className="order-3 space-y-3.5 min-[1400px]:h-full min-[1400px]:overflow-y-auto pr-0.5" data-testid="hud-right-col">
            <ApprovalsPanel />
            <HudPanel title="Jarvis Assistant" icon={MessageSquare} testId="panel-assistant"
              right={aiStatus?.ai_status === "not_connected" && <span data-testid="assistant-not-connected" className="font-mono text-[8px] uppercase tracking-wider text-jarvis-amber">AI not connected</span>}>
              <div className="h-[300px]">
                <ConversationPanel ref={assistantRef} onStateChange={setCoreState} onListeningChange={setMicOn} />
              </div>
            </HudPanel>
            <RecentActivityPanel />
            <AiUsagePanel />
            <QuickCommandsPanel
              onBriefing={() => onQuick("briefing")}
              onStatus={() => onQuick("status")}
              onStrained={() => assistantRef.current?.sendText("Which agents are strained?")}
              onTasks={() => assistantRef.current?.sendText("Summarize active tasks")}
              onImage={() => assistantRef.current?.prefill("generate image of ")}
            />
          </div>
        </div>
      </div>

      {emgConfirm && (
        <EmergencyStopDialog onCancel={() => setEmgConfirm(false)} onConfirm={confirmEmergency} />
      )}
    </div>
  );
}

function EmergencyStopDialog({ onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="emergency-dialog">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onCancel} data-testid="emergency-dialog-backdrop" />
      <div className="relative w-full max-w-md jv-panel hud-chamfer hud-bracket border-jarvis-red/50" style={{ borderColor: "rgba(255,63,88,0.5)" }}>
        <div className="flex items-center gap-2.5 px-4 py-3 border-b border-jarvis-red/30 bg-jarvis-red/10">
          <AlertTriangle className="w-5 h-5 text-jarvis-red" />
          <h3 className="font-brand text-sm font-bold uppercase tracking-[0.14em] text-jarvis-red">Emergency Stop</h3>
        </div>
        <div className="p-4 space-y-3">
          <p className="font-ui text-[15px] text-jarvis-cyanBright/90 leading-snug">
            This pauses all <span className="text-jarvis-red font-semibold">Jarvis app-managed workers</span> and cancels their current jobs.
            Your external website and wallet servers are not affected.
          </p>
          <ul className="font-ui text-sm text-jarvis-cyanBright/60 space-y-1 pl-1">
            <li>App workers stop; queued jobs remain paused</li>
            <li>Restore resumes only workers that were enabled</li>
            <li>Remote VPS telemetry is not connected; emergency controls affect app-managed workers only</li>
          </ul>
          <p className="font-ui text-[13px] text-jarvis-cyanBright/45">You can restore the fleet afterwards from the same control.</p>
          <div className="flex items-center justify-end gap-2.5 pt-1">
            <button onClick={onCancel} data-testid="emergency-cancel-btn"
              className="font-brand text-[11px] font-semibold uppercase tracking-wider px-4 py-2 border border-jarvis-cyan/30 text-jarvis-cyanBright/80 hover:bg-jarvis-cyan/10 hud-chamfer">
              Cancel
            </button>
            <button onClick={onConfirm} data-testid="emergency-confirm-btn" autoFocus
              className="font-brand text-[11px] font-bold uppercase tracking-wider px-4 py-2 border-2 border-jarvis-red bg-jarvis-red/90 text-white hover:bg-jarvis-red hud-chamfer shadow-[0_0_20px_rgba(255,63,88,0.5)]">
              Engage Shutdown
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
